/**
 * Mechanical results view: renders the placed equipment schedule + the
 * per-space load screening (delegated ASHRAE 62.1 ventilation demand and the
 * ASHRAE Handbook—Fundamentals equipment-capacity screen) from the raw
 * mechanical Output, plus the BoQ CSV export link.
 *
 * Mirrors `ElectricalResults.tsx` exactly — same style constants, same
 * card/table/empty-state shapes, same palette. This is the istari prototype's
 * smoke-view so the discipline is clickable end-to-end.
 */
import type { CSSProperties } from "react";
import type {
  ApiSpace,
  MechanicalBoq,
  MechanicalEquipment,
  SpaceLoadScreening,
} from "../api/types";

const sectionTitle: CSSProperties = {
  margin: "1.2rem 0 0.5rem",
  fontSize: "1rem",
};

const card: CSSProperties = {
  background: "#21213a",
  borderRadius: 8,
  padding: "0.8rem 1rem",
  marginBottom: "0.8rem",
};

const table: CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "0.85rem",
};

const th: CSSProperties = {
  textAlign: "left",
  padding: "0.3rem 0.5rem",
  color: "#8e8eb8",
  borderBottom: "1px solid #33335a",
  fontWeight: 600,
};

const td: CSSProperties = {
  padding: "0.3rem 0.5rem",
  borderBottom: "1px solid #2a2a44",
};

const cfm = (n: number) => `${Math.round(n).toLocaleString()} cfm`;

/** Human label for an equipment kind (matches the engine's kind_phrase). */
const KIND_LABELS: Record<string, string> = {
  rooftop_unit: "Rooftop unit (RTU)",
  air_handling_unit: "Air handling unit (AHU)",
  pump: "Pump",
  vav_terminal: "VAV terminal",
};

/** The per-kind capacity cell: tons for RTU/AHU, gpm for pump, cfm for VAV. */
function capacityText(e: MechanicalEquipment): string {
  if (e.capacity_tons != null) return `${e.capacity_tons.toFixed(1)} tons`;
  if (e.capacity_gpm != null) return `${Math.round(e.capacity_gpm)} gpm`;
  if (e.cfm != null) {
    return e.inlet_in != null ? `${cfm(e.cfm)} @ ${e.inlet_in}" inlet` : cfm(e.cfm);
  }
  return "—";
}

/** Whether this row claims a sized capacity at all (a row with none needs no
 * citation — there is no sized value to attribute). */
function hasSizedCapacity(e: MechanicalEquipment): boolean {
  return e.capacity_tons != null || e.capacity_gpm != null || e.cfm != null;
}

/**
 * The row-level basis for a sized capacity, read from the SAME provenance the
 * sizing engine attaches to the element (`provenance.citation` — the ASHRAE
 * 62.1 table for a 62.1-derived cfm, else the Handbook screening chapter),
 * exactly as the load-screening table pulls its per-row citation.
 *
 * A sized capacity with no citation is a defect, not a display choice — so it
 * renders a loud marker instead of a silent em-dash, which would read as
 * "intentionally not applicable".
 */
function capacityCitation(e: MechanicalEquipment): string {
  const citation = e.provenance?.citation?.trim();
  if (citation) return citation;
  return hasSizedCapacity(e) ? "⚠ no citation on record" : "—";
}

/** Human label for a space id: `"1A01 PATIENT ADMIN. RECEPT."` — the model's
 * `name` (room number/tag) plus its `long_name` (description), never the raw
 * ULID (the identity convention the engines follow in finding prose). Falls
 * back to whichever part exists, then to the id when the space isn't in the
 * model rows. */
function spaceLabel(id: string, byId: Map<string, ApiSpace>): string {
  const s = byId.get(id);
  if (!s) return id;
  const tag = s.name?.trim();
  const description = s.long_name?.trim();
  if (tag && description) return `${tag} ${description}`;
  return tag || description || id;
}

export interface MechanicalResultsProps {
  /** Placed equipment from the raw mechanical Output. */
  equipment?: MechanicalEquipment[];
  /** Per-space load screening from the raw mechanical Output. */
  loadScreening?: SpaceLoadScreening[];
  /** BoQ rollup; `null` before generation. */
  boq?: MechanicalBoq | null;
  /** Model spaces, used to render human space labels instead of raw ULIDs. */
  spaces?: ApiSpace[];
  systemId: string | null;
}

export function MechanicalResults({
  equipment = [],
  loadScreening = [],
  boq = null,
  spaces = [],
  systemId,
}: MechanicalResultsProps) {
  const spacesById = new Map(spaces.map((s) => [s.id, s]));
  const boqCounts = Object.entries(boq?.equipment_counts ?? {});
  const boqTons = boq?.total_capacity_tons_by_kind ?? {};
  const boqCitations = boq?.total_capacity_citation_by_kind ?? {};
  // Zones sized off the Handbook screen because no 62.1 row applied — surfaced
  // as a count so a mostly-unclassified model reads honestly at a glance.
  const fallbackCount = loadScreening.filter((r) => r.design_method === "screening_default").length;

  return (
    <section data-testid="mechanical-results">
      <h2 style={sectionTitle}>Equipment schedule</h2>
      {equipment.length > 0 ? (
        <div style={card} data-testid="mechanical-equipment-schedule">
          <div
            data-testid="mechanical-screening-flag"
            style={{
              margin: "0 0 0.6rem",
              padding: "0.4rem 0.6rem",
              borderRadius: 6,
              background: "#3a3320",
              color: "#e5c07b",
              fontSize: "0.8rem",
            }}
          >
            ⚠ Estimating-grade screening — capacities are ASHRAE Handbook&#8212;Fundamentals cfm/ft²
            (or cfm/ton) cross-checks snapped up the catalog ladder, not a heat-balance load
            calculation or equipment selection.
          </div>
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>Tag</th>
                <th style={th}>Equipment</th>
                <th style={th}>Capacity</th>
                <th style={th}>Capacity basis (citation)</th>
                <th style={th}>Host space</th>
                <th style={th}>Clearance (IMC §306)</th>
              </tr>
            </thead>
            <tbody>
              {equipment.map((e) => (
                <tr key={e.id}>
                  <td style={td}>{e.tag ?? e.id}</td>
                  <td style={td}>{KIND_LABELS[e.kind] ?? e.kind}</td>
                  <td style={td}>{capacityText(e)}</td>
                  <td
                    data-testid={`mechanical-equipment-citation-${e.id}`}
                    style={{ ...td, color: "#8e8eb8", fontSize: "0.78rem" }}
                  >
                    {capacityCitation(e)}
                  </td>
                  <td style={td}>{spaceLabel(e.space_id, spacesById)}</td>
                  <td
                    style={{
                      ...td,
                      color: e.clearance_ok ? "#98c379" : "#e06c75",
                      fontWeight: 600,
                    }}
                  >
                    {e.clearance_ok ? "✓ clears" : "✗ SHORTFALL"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p style={{ color: "#8e8eb8" }} data-testid="mechanical-equipment-empty">
          No equipment yet.
        </p>
      )}

      <h2 style={sectionTitle}>
        Load screening (ASHRAE 62.1 §6.2.2.1 / Handbook&#8212;Fundamentals)
      </h2>
      {loadScreening.length > 0 ? (
        <div style={card} data-testid="mechanical-load-screening">
          {fallbackCount > 0 && (
            <div
              data-testid="mechanical-fallback-flag"
              style={{
                margin: "0 0 0.6rem",
                padding: "0.4rem 0.6rem",
                borderRadius: 6,
                background: "#3a3320",
                color: "#e5c07b",
                fontSize: "0.8rem",
              }}
            >
              ⚠ {fallbackCount} of {loadScreening.length} zones have no ASHRAE 62.1 Table 6.2.2.1
              row — their design airflow is a Handbook cfm/ft² screening stand-in, not a cited
              ventilation rate. Classify those spaces&#39; occupancy and re-generate.
            </div>
          )}
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>Space</th>
                <th style={th}>Design airflow</th>
                <th style={th}>Basis</th>
                <th style={th}>Citation</th>
                <th style={th}>Handbook cfm/ft&#178;</th>
              </tr>
            </thead>
            <tbody>
              {loadScreening.map((r) => {
                const fallback = r.design_method === "screening_default";
                return (
                  <tr key={r.space_id}>
                    <td style={td}>{spaceLabel(r.space_id, spacesById)}</td>
                    <td style={td}>{cfm(r.design_cfm)}</td>
                    <td style={{ ...td, color: fallback ? "#e5c07b" : "#98c379" }}>
                      {fallback ? "Handbook screen" : "62.1 Vbz"}
                    </td>
                    <td style={{ ...td, color: "#8e8eb8", fontSize: "0.78rem" }}>
                      {r.design_citation || "—"}
                    </td>
                    <td style={td}>{cfm(r.screening_cfm)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p style={{ color: "#8e8eb8" }} data-testid="mechanical-load-screening-empty">
          No load screening yet.
        </p>
      )}

      <h2 style={sectionTitle}>Bill of quantities</h2>
      {boqCounts.length > 0 ? (
        <div style={card} data-testid="mechanical-boq">
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>Equipment</th>
                <th style={th}>Count</th>
                <th style={th}>Total capacity</th>
                <th style={th}>Capacity basis (citation)</th>
              </tr>
            </thead>
            <tbody>
              {boqCounts.map(([kind, count]) => {
                const tons = boqTons[kind];
                const citation = boqCitations[kind]?.trim();
                return (
                  <tr key={kind}>
                    <td style={td}>{KIND_LABELS[kind] ?? kind}</td>
                    <td style={td}>{count}</td>
                    <td style={td}>{tons != null ? `${tons.toFixed(1)} tons` : "—"}</td>
                    <td
                      data-testid={`mechanical-boq-citation-${kind}`}
                      style={{ ...td, color: "#8e8eb8", fontSize: "0.78rem" }}
                    >
                      {/* A rolled-up sized value must show its basis; a kind with
                          no sized capacity has nothing to attribute. */}
                      {tons == null ? "—" : citation || "⚠ no citation on record"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p style={{ color: "#8e8eb8" }} data-testid="mechanical-boq-empty">
          No bill of quantities yet.
        </p>
      )}

      {systemId && (
        <p style={{ fontSize: "0.85rem" }}>
          <strong>Exports:</strong>{" "}
          <a href={`/api/mep-systems/${systemId}/output`} target="_blank" rel="noreferrer">
            Mechanical output (JSON)
          </a>
        </p>
      )}
    </section>
  );
}
